import React from 'react';
import { X, BookOpen, ShieldCheck, TrendingUp, Wallet, Zap, ChevronRight, ListChecks } from 'lucide-react';

export const HelpModal = ({ isOpen, onClose, onOpenManual }: { isOpen: boolean, onClose: () => void, onOpenManual: () => void }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 animate-fade-in">
      <div className="absolute inset-0 bg-navy/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl bg-white rounded-4xl shadow-2xl overflow-hidden animate-slide-up flex flex-col max-h-[90vh]">
        <div className="bg-navy p-8 text-white flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <BookOpen className="text-electric" />
            <div>
              <h2 className="text-xl font-extrabold tracking-tight">How can we help?</h2>
              <p className="text-[10px] text-sky font-bold uppercase tracking-widest">StackUp Simple Guide</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-8 overflow-y-auto custom-scrollbar space-y-10">
          {/* Quick Start Guide */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks className="text-electric" size={20} />
              <h3 className="text-xs font-black text-navy uppercase tracking-widest">Getting Started in 4 Steps</h3>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <GuideStep 
                step="01" 
                title="Add Your Income" 
                desc="Go to the Ledger. Record your salary or any money you receive as 'Income'." 
              />
              <GuideStep 
                step="02" 
                title="Set Your Budget" 
                desc="Go to the Planner. Decide how much you want to spend on things like food or rent." 
              />
              <GuideStep 
                step="03" 
                title="Track Your Wealth" 
                desc="Go to the Wealth tab. Add your bank balances and any debts you owe." 
              />
              <GuideStep 
                step="04" 
                title="Check Your Progress" 
                desc="Use the Dashboard to see your savings rate and financial health score." 
              />
            </div>
          </section>

          {/* Full Manual Link */}
          <div className="p-6 bg-electric/5 rounded-3xl border border-electric/10 flex flex-col items-center text-center space-y-4">
            <div className="p-3 bg-white rounded-2xl shadow-sm text-electric">
              <BookOpen size={24} />
            </div>
            <div>
              <h4 className="font-bold text-navy">Need more details?</h4>
              <p className="text-sm text-slate-500">We have a full, easy-to-read user manual that covers everything in depth.</p>
            </div>
            <button 
              onClick={onOpenManual}
              className="px-6 py-3 bg-navy text-white rounded-xl font-bold text-sm hover:bg-electric transition-all flex items-center gap-2"
            >
              Open Full User Manual <ChevronRight size={16} />
            </button>
          </div>

          {/* Definitions */}
          <section className="space-y-8">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="text-electric" size={20} />
              <h3 className="text-xs font-black text-navy uppercase tracking-widest">Simple Definitions</h3>
            </div>
            <GlossarySection 
              icon={<ShieldCheck className="text-electric" />}
              title="Net Worth"
              description="This is everything you own minus everything you owe. It's the best way to see your overall financial progress."
            />
            <GlossarySection 
              icon={<TrendingUp className="text-emerald-500" />}
              title="Savings Rate"
              description="The percentage of your income that you keep after paying all your bills. Higher is better!"
            />
          </section>
        </div>
      </div>
    </div>
  );
};

const GuideStep = ({ step, title, desc }: any) => (
  <div className="flex gap-4 p-4 bg-slate-50 border border-slate-100 rounded-2xl group hover:border-electric/30 transition-all">
    <div className="text-electric font-black text-lg font-mono opacity-40 group-hover:opacity-100 transition-opacity">{step}</div>
    <div className="space-y-1">
      <div className="text-xs font-black text-navy uppercase tracking-wide flex items-center gap-2">
        {title} <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
      </div>
      <p className="text-[11px] text-slate-500 leading-normal">{desc}</p>
    </div>
  </div>
);

const GlossarySection = ({ icon, title, description }: any) => (
  <div className="flex gap-5">
    <div className="p-3 bg-slate-50 rounded-2xl shrink-0 h-fit">
      {icon}
    </div>
    <div className="space-y-1">
      <h3 className="text-sm font-black text-navy uppercase tracking-wide">{title}</h3>
      <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
    </div>
  </div>
);